import React, { ReactNode, createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { I18n } from '@aws-amplify/core';
import { Skeleton } from 'antd';

import type { Context, ReportParams, State } from './types';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetResult } from '../../hooks/useGetResult';
import type { BaseContext } from '@/types';
import { useSearchParams } from '@/hooks/v1';

const RosaResultContext = createContext<Context>({} as Context);

interface RosaResultProviderProps {
	children: ReactNode;
}

export function RosaResultProvider({ children }: Readonly<RosaResultProviderProps>) {
	const { file_id } = useParams<ReportParams>();
	const { organization, company } = useApplicationContext();
	const { organization_id = organization?.id, company_id = company?.id } = useSearchParams<BaseContext>();

	const {
		isError,
		isInitialLoading,
		data: rosaResult
	} = useGetResult({
		id: file_id,
		finished: true,
		company_id: company_id,
		organization_id: organization_id
	});

	if (isInitialLoading) {
		return <Skeleton active />;
	}

	if (isError || !rosaResult) {
		return <span>{I18n.get('Oops... Something went wrong!')}</span>;
	}

	const states: State = {
		rosaResult,
		organization_id,
		company_id,
		file_id
	};

	const context = {
		...states
	};

	return <RosaResultContext.Provider value={context}>{children}</RosaResultContext.Provider>;
}

export function useRosaResultContext() {
	const context = useContext(RosaResultContext);
	return context;
}
